/**
 * Analytics Page.
 *
 * Tabbed analytics dashboard with inventory summary, profitability,
 * credit impact, cash flow, sales performance, and tax reporting.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import ExportButton from "@/components/ui/ExportButton";
import {
  useInventorySummary,
  useProfitability,
  useCreditImpact,
  useCashFlow,
  useSalesPerformance,
  useTaxReport,
} from "@/features/analytics/api";
import Badge from "@/components/ui/Badge";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

type Tab =
  | "inventory"
  | "profitability"
  | "credit"
  | "carrying"
  | "cashflow"
  | "sales"
  | "tax";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );

function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState<Tab>("inventory");

  const tabs: { key: Tab; label: string }[] = [
    { key: "inventory", label: "Inventory" },
    { key: "profitability", label: "Profitability" },
    { key: "credit", label: "Credit Impact" },
    { key: "carrying", label: "Carrying Costs" },
    { key: "cashflow", label: "Cash Flow" },
    { key: "sales", label: "Sales Performance" },
    { key: "tax", label: "Tax Report" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          Analytics
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Financial intelligence and reporting.
        </p>
      </div>

      <div className="border-b border-[var(--border-default)]">
        <nav className="flex gap-6 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap border-b-2 pb-3 text-sm font-medium transition-colors ${activeTab === tab.key ? "border-[var(--color-interactive-primary)] text-[var(--color-interactive-primary)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-secondary)]"}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "inventory" && <InventoryTab />}
      {activeTab === "profitability" && <ProfitabilityTab />}
      {activeTab === "credit" && <CreditImpactTab />}
      {activeTab === "carrying" && <CarryingCostsTab />}
      {activeTab === "cashflow" && <CashFlowTab />}
      {activeTab === "sales" && <SalesTab />}
      {activeTab === "tax" && <TaxTab />}
    </div>
  );
}

function InventoryTab() {
  const { data, isLoading } = useInventorySummary();
  if (isLoading) return <LoadingSpinner className="py-12" />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-sm text-[var(--text-secondary)]">Total Assets</p>
          <p className="mt-1 text-3xl font-bold text-[var(--text-primary)]">
            {data.total_assets}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--text-secondary)]">Total Value</p>
          <p className="mt-1 text-3xl font-bold text-[var(--text-primary)]">
            {fmt(data.total_value)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--text-secondary)]">
            Avg Days in Inventory
          </p>
          <p className="mt-1 text-3xl font-bold text-[var(--text-primary)]">
            {data.avg_days_in_inventory}
          </p>
        </Card>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <h3 className="mb-3 text-sm font-medium text-[var(--text-primary)]">
            By Status
          </h3>
          <div className="space-y-2">
            {data.by_status.map((s) => (
              <div key={s.status} className="flex items-center justify-between">
                <span className="text-sm text-[var(--text-secondary)]">
                  {s.status}
                </span>
                <Badge>{s.count}</Badge>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <h3 className="mb-3 text-sm font-medium text-[var(--text-primary)]">
            By Category
          </h3>
          <div className="space-y-2">
            {data.by_category.map((c) => (
              <div
                key={c.category}
                className="flex items-center justify-between"
              >
                <span className="text-sm capitalize text-[var(--text-secondary)]">
                  {c.category}
                </span>
                <span className="text-sm font-medium">
                  {c.count} ({fmt(c.value)})
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function ProfitabilityTab() {
  const { data, isLoading } = useProfitability();
  if (isLoading) return <LoadingSpinner className="py-12" />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card>
          <p className="text-xs text-[var(--text-secondary)]">Revenue</p>
          <p className="mt-1 text-xl font-bold">{fmt(data.total_revenue)}</p>
        </Card>
        <Card>
          <p className="text-xs text-[var(--text-secondary)]">COGS</p>
          <p className="mt-1 text-xl font-bold">{fmt(data.total_cogs)}</p>
        </Card>
        <Card>
          <p className="text-xs text-[var(--text-secondary)]">Gross Profit</p>
          <p className="mt-1 text-xl font-bold text-[var(--text-success)]">
            {fmt(data.total_gross_profit)}
          </p>
        </Card>
        <Card>
          <p className="text-xs text-[var(--text-secondary)]">Avg Margin</p>
          <p className="mt-1 text-xl font-bold">{data.avg_margin_pct}%</p>
        </Card>
      </div>
      {data.items.length > 0 ? (
        <Card padding="none">
          <div className="flex items-center justify-between border-b border-[var(--border-default)] px-4 py-3">
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              Per-Asset Profitability
            </h3>
            <ExportButton
              url="/api/v1/analytics/profitability/export"
              fallbackFilename="profitability"
              size="sm"
              variant="ghost"
            />
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-[var(--surface-sunken)] text-left text-xs font-medium text-[var(--text-secondary)]">
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2 text-right">Sale</th>
                <th className="px-4 py-2 text-right">COGS</th>
                <th className="px-4 py-2 text-right">Profit</th>
                <th className="px-4 py-2 text-right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr
                  key={item.asset_id}
                  className="border-b border-[var(--border-subtle)]"
                >
                  <td className="px-4 py-2 font-mono text-xs">{item.sku}</td>
                  <td className="px-4 py-2">{item.name}</td>
                  <td className="px-4 py-2 text-right">
                    {fmt(item.sale_price)}
                  </td>
                  <td className="px-4 py-2 text-right text-[var(--text-secondary)]">
                    {fmt(item.cogs)}
                  </td>
                  <td
                    className={`px-4 py-2 text-right font-medium ${item.gross_profit >= 0 ? "text-[var(--text-success)]" : "text-[var(--text-danger)]"}`}
                  >
                    {fmt(item.gross_profit)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {item.margin_pct === null ? (
                      <span
                        className="text-[var(--text-tertiary)]"
                        title="No cost recorded for this line, so its margin cannot be calculated."
                      >
                        —
                      </span>
                    ) : (
                      `${item.margin_pct}%`
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : (
        <Card>
          <p className="text-center text-sm text-[var(--text-tertiary)] py-8">
            No sales data yet.
          </p>
        </Card>
      )}
    </div>
  );
}

function CreditImpactTab() {
  const { data, isLoading } = useCreditImpact();
  if (isLoading) return <LoadingSpinner className="py-12" />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <p className="text-sm text-[var(--text-secondary)]">
            Total Outstanding
          </p>
          <p className="mt-1 text-2xl font-bold text-[var(--text-danger)]">
            {fmt(data.total_outstanding)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--text-secondary)]">
            Total Interest Expense
          </p>
          <p className="mt-1 text-2xl font-bold text-[var(--text-warning)]">
            {fmt(data.total_interest_expense)}
          </p>
          <p className="mt-1 text-xs text-[var(--text-tertiary)]">
            Tracked separately from COGS per accounting principles
          </p>
        </Card>
      </div>
      {data.accounts.length > 0 && (
        <Card padding="none">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-[var(--surface-sunken)] text-left text-xs font-medium text-[var(--text-secondary)]">
                <th className="px-4 py-2">Account</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2 text-right">Charges</th>
                <th className="px-4 py-2 text-right">Interest</th>
                <th className="px-4 py-2 text-right">Payments</th>
                <th className="px-4 py-2 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.accounts.map((a) => (
                <tr
                  key={a.account_id}
                  className="border-b border-[var(--border-subtle)]"
                >
                  <td className="px-4 py-2 font-medium">{a.account_name}</td>
                  <td className="px-4 py-2 text-[var(--text-secondary)]">
                    {a.account_type.replace("_", " ")}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {fmt(a.total_charges)}
                  </td>
                  <td className="px-4 py-2 text-right text-[var(--text-warning)]">
                    {fmt(a.total_interest)}
                  </td>
                  <td className="px-4 py-2 text-right text-[var(--text-success)]">
                    {fmt(a.total_payments)}
                  </td>
                  <td className="px-4 py-2 text-right font-medium">
                    {fmt(a.current_balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function CashFlowTab() {
  const { data, isLoading } = useCashFlow();
  if (isLoading) return <LoadingSpinner className="py-12" />;
  if (!data) return null;

  const allPeriods = [...data.historical, ...data.projected];

  return (
    <div className="space-y-6">
      <Card padding="none">
        <div className="border-b border-[var(--border-default)] px-4 py-3">
          <h3 className="text-sm font-medium text-[var(--text-primary)]">
            Cash Flow — Historical &amp; Projected
          </h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-[var(--surface-sunken)] text-left text-xs font-medium text-[var(--text-secondary)]">
              <th className="px-4 py-2">Period</th>
              <th className="px-4 py-2"></th>
              <th className="px-4 py-2 text-right">Inflow</th>
              <th className="px-4 py-2 text-right">Outflow</th>
              <th className="px-4 py-2 text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {allPeriods.map((p, i) => {
              const isProjected = i >= data.historical.length;
              return (
                <tr
                  key={p.period}
                  className={`border-b border-[var(--border-subtle)] ${isProjected ? "bg-[var(--fill-info)]/50" : ""}`}
                >
                  <td className="px-4 py-2 font-mono text-xs">{p.period}</td>
                  <td className="px-4 py-2">
                    {isProjected && <Badge variant="info">projected</Badge>}
                  </td>
                  <td className="px-4 py-2 text-right text-[var(--text-success)]">
                    {fmt(p.inflow)}
                  </td>
                  <td className="px-4 py-2 text-right text-[var(--text-danger)]">
                    {fmt(p.outflow)}
                  </td>
                  <td
                    className={`px-4 py-2 text-right font-medium ${p.net >= 0 ? "text-[var(--text-success)]" : "text-[var(--text-danger)]"}`}
                  >
                    {fmt(p.net)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function SalesTab() {
  const { data, isLoading } = useSalesPerformance();
  if (isLoading) return <LoadingSpinner className="py-12" />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <p className="text-sm text-[var(--text-secondary)]">Total Sales</p>
          <p className="mt-1 text-3xl font-bold">{data.total_sales}</p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--text-secondary)]">Total Revenue</p>
          <p className="mt-1 text-3xl font-bold text-[var(--text-success)]">
            {fmt(data.total_revenue)}
          </p>
        </Card>
      </div>
      {data.salespeople.length > 0 && (
        <Card padding="none">
          <div className="border-b border-[var(--border-default)] px-4 py-3">
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              Leaderboard
            </h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-[var(--surface-sunken)] text-left text-xs font-medium text-[var(--text-secondary)]">
                <th className="px-4 py-2">#</th>
                <th className="px-4 py-2">Salesperson</th>
                <th className="px-4 py-2 text-right">Sales</th>
                <th className="px-4 py-2 text-right">Revenue</th>
                <th className="px-4 py-2 text-right">Commission</th>
              </tr>
            </thead>
            <tbody>
              {data.salespeople.map((s, i) => (
                <tr
                  key={s.user_id}
                  className="border-b border-[var(--border-subtle)]"
                >
                  <td className="px-4 py-2 text-[var(--text-tertiary)]">
                    {i + 1}
                  </td>
                  <td className="px-4 py-2 font-medium">{s.full_name}</td>
                  <td className="px-4 py-2 text-right">{s.total_sales}</td>
                  <td className="px-4 py-2 text-right">
                    {fmt(s.total_revenue)}
                  </td>
                  <td className="px-4 py-2 text-right text-[var(--color-interactive-primary)]">
                    {fmt(s.total_commission)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function TaxTab() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const { data, isLoading } = useTaxReport(year);
  if (isLoading) return <LoadingSpinner className="py-12" />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-[var(--text-secondary)]">
          Tax Year:
        </label>
        <select
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
          className="rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
        >
          {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <ExportButton
          url="/api/v1/analytics/tax-report/export"
          params={{ year }}
          fallbackFilename={`tax-report-${year}`}
          size="sm"
          variant="ghost"
        />
      </div>

      <Card>
        <h3 className="mb-4 text-sm font-medium text-[var(--text-primary)]">
          Schedule C Summary — {data.period_start} to {data.period_end}
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between border-b border-[var(--border-subtle)] pb-2">
            <span className="text-sm text-[var(--text-secondary)]">
              Gross Receipts
            </span>
            <span className="text-sm font-medium">
              {fmt(data.gross_receipts)}
            </span>
          </div>
          <div className="flex justify-between border-b border-[var(--border-subtle)] pb-2">
            <span className="text-sm text-[var(--text-secondary)]">
              Cost of Goods Sold (COGS)
            </span>
            <span className="text-sm font-medium text-[var(--text-danger)]">
              ({fmt(data.total_cogs)})
            </span>
          </div>
          <div className="flex justify-between border-b border-[var(--border-default)] pb-2">
            <span className="text-sm font-medium text-[var(--text-primary)]">
              Gross Profit
            </span>
            <span className="text-sm font-bold text-[var(--text-primary)]">
              {fmt(data.gross_profit)}
            </span>
          </div>
          <div className="flex justify-between border-b border-[var(--border-subtle)] pb-2">
            <span className="text-sm text-[var(--text-secondary)]">
              Interest Expense (non-operating)
            </span>
            <span className="text-sm font-medium text-[var(--text-warning)]">
              ({fmt(data.interest_expense)})
            </span>
          </div>
          <div className="flex justify-between pt-1">
            <span className="text-sm font-bold text-[var(--text-primary)]">
              Net Profit Before Tax
            </span>
            <span
              className={`text-lg font-bold ${data.net_profit_before_tax >= 0 ? "text-[var(--text-success)]" : "text-[var(--text-danger)]"}`}
            >
              {fmt(data.net_profit_before_tax)}
            </span>
          </div>
        </div>
        <p className="mt-4 text-xs text-[var(--text-tertiary)]">
          COGS includes purchase price, freight, shipping, and refurbishment
          costs. Interest on business credit is tracked separately as a
          non-operating expense per IRS guidelines.
        </p>
      </Card>
    </div>
  );
}

// ==========================================================================
// Carrying Costs Tab
// ==========================================================================

interface CarryingCostAsset {
  asset_id: string;
  sku: string;
  name: string;
  category: string;
  total_cogs: number;
  days_in_inventory: number;
  daily_carrying_cost: number;
  total_carrying_cost: number;
}

interface CarryingCostData {
  total_carrying_cost: number;
  total_daily_interest: number;
  monthly_estimate: number;
  asset_count: number;
  by_category: Array<{
    category: string;
    count: number;
    total_value: number;
    total_carrying_cost: number;
    avg_days_in_inventory: number;
  }>;
  by_account: Array<{
    account_name: string;
    current_balance: number;
    interest_rate: number;
    daily_interest: number;
  }>;
  top_assets: CarryingCostAsset[];
}

function CarryingCostsTab() {
  const { data, isLoading } = useQuery<CarryingCostData>({
    queryKey: ["analytics", "carrying-costs"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/analytics/carrying-costs")).data,
  });

  if (isLoading) return <LoadingSpinner className="py-12" />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Summary KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Card padding="sm">
          <p className="text-xs text-[var(--text-secondary)]">
            Total Carrying Cost
          </p>
          <p className="text-xl font-bold text-[var(--text-danger)]">
            {fmt(data.total_carrying_cost)}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-xs text-[var(--text-secondary)]">
            Monthly Estimate
          </p>
          <p className="text-xl font-bold text-[var(--text-primary)]">
            {fmt(data.monthly_estimate)}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-xs text-[var(--text-secondary)]">Daily Interest</p>
          <p className="text-xl font-bold text-[var(--text-primary)]">
            {fmt(data.total_daily_interest)}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-xs text-[var(--text-secondary)]">Assets</p>
          <p className="text-xl font-bold text-[var(--text-primary)]">
            {data.asset_count}
          </p>
        </Card>
      </div>

      {/* By Category */}
      {data.by_category.length > 0 && (
        <Card>
          <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3">
            By Category
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-default)] text-left text-xs text-[var(--text-secondary)]">
                <th className="pb-2">Category</th>
                <th className="pb-2 text-center">Count</th>
                <th className="pb-2 text-right">Value</th>
                <th className="pb-2 text-right">Carrying Cost</th>
                <th className="pb-2 text-right">Avg DII</th>
              </tr>
            </thead>
            <tbody>
              {data.by_category.map((c) => (
                <tr
                  key={c.category}
                  className="border-b border-[var(--border-subtle)] last:border-0"
                >
                  <td className="py-2 capitalize">{c.category}</td>
                  <td className="py-2 text-center">{c.count}</td>
                  <td className="py-2 text-right">{fmt(c.total_value)}</td>
                  <td className="py-2 text-right text-[var(--text-danger)]">
                    {fmt(c.total_carrying_cost)}
                  </td>
                  <td className="py-2 text-right">
                    {c.avg_days_in_inventory}d
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* By Credit Account */}
      {data.by_account.length > 0 && (
        <Card>
          <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3">
            By Credit Account
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-default)] text-left text-xs text-[var(--text-secondary)]">
                <th className="pb-2">Account</th>
                <th className="pb-2 text-right">Balance</th>
                <th className="pb-2 text-right">Rate</th>
                <th className="pb-2 text-right">Daily Interest</th>
              </tr>
            </thead>
            <tbody>
              {data.by_account.map((a) => (
                <tr
                  key={a.account_name}
                  className="border-b border-[var(--border-subtle)] last:border-0"
                >
                  <td className="py-2">{a.account_name}</td>
                  <td className="py-2 text-right">{fmt(a.current_balance)}</td>
                  <td className="py-2 text-right">
                    {(a.interest_rate * 100).toFixed(2)}%
                  </td>
                  <td className="py-2 text-right text-[var(--text-danger)]">
                    {fmt(a.daily_interest)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Top Assets by Carrying Cost */}
      {data.top_assets.length > 0 && (
        <Card>
          <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3">
            Most Expensive to Hold
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-default)] text-left text-xs text-[var(--text-secondary)]">
                <th className="pb-2">Asset</th>
                <th className="pb-2 text-right">COGS</th>
                <th className="pb-2 text-right">Days</th>
                <th className="pb-2 text-right">Daily Cost</th>
                <th className="pb-2 text-right">Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.top_assets.slice(0, 10).map((a) => (
                <tr
                  key={a.asset_id}
                  className="border-b border-[var(--border-subtle)] last:border-0"
                >
                  <td className="py-2">
                    <span className="font-mono text-xs text-brand-600 mr-2">
                      {a.sku}
                    </span>
                    {a.name}
                  </td>
                  <td className="py-2 text-right">{fmt(a.total_cogs)}</td>
                  <td className="py-2 text-right">{a.days_in_inventory}d</td>
                  <td className="py-2 text-right">
                    {fmt(a.daily_carrying_cost)}
                  </td>
                  <td className="py-2 text-right font-semibold text-[var(--text-danger)]">
                    {fmt(a.total_carrying_cost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

export default AnalyticsPage;
